/**
 * Pure, framework-free `<head>` splicing for the build-time SEO emitter.
 *
 * Given a built `index.html` shell and a set of resolved head tags (from
 * `buildHeadTags`), this produces a new HTML string where the SEO-managed
 * tags — `<title>`, `description`, `keywords`, `robots`, `canonical`,
 * verification, all `og:*` / `article:*` properties, all `twitter:*` names,
 * and `application/ld+json` scripts — have been **replaced** (not duplicated)
 * with the resolved set.
 *
 * Design goals:
 *   - **Idempotent**: running it twice yields the same output (it strips the
 *     managed tags first, then re-inserts the canonical set).
 *   - **Deterministic**: tag order is fixed by `renderHeadTags`.
 *   - **Conservative**: only tags we own are touched. Charset, viewport, CSP,
 *     theme-color, favicons, stylesheets, and the app script are left intact.
 *
 * This is intentionally regex-based (no DOM dependency) to mirror the existing
 * `dcsCdnImagePlugin` post-build HTML rewriting and to keep the emitter free of
 * heavy parser deps at build time.
 */

import type { ResolvedHeadTags } from './headTags'

/** Escape a string for safe inclusion in a double-quoted HTML attribute. */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Escape a JSON-LD string for safe inclusion inside an HTML `<script>` element.
 *
 * A `<script type="application/ld+json">` is a DATA block, but the HTML parser
 * still scans its raw text for `</script` (and `<!--`) — so untrusted CMS free
 * text (review bodies, FAQ Q&A, blog descriptions) carrying `</script>` could
 * break out of the script and inject markup (stored XSS).
 *
 * Per OWASP "JSON in an HTML context", we escape the three HTML-significant
 * characters as their `\uXXXX` JSON escapes. Because these characters only ever
 * appear INSIDE JSON string literals (never as JSON structure), the result is
 * still byte-for-byte valid JSON that `JSON.parse` round-trips:
 *   `<` → `<`  (defeats `</script>` and `<!--` breakout)
 *   `>` → `>`
 *   `&` → `&`
 *
 * Exported so BOTH static-SEO sinks (the SPA `spliceHeadHtml` path AND the
 * VitePress `transformPageData` head emit) share one guard.
 */
export function escapeJsonLd(json: string): string {
  return json
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
}

/** `name=` meta tags that the emitter owns and will replace. */
const MANAGED_META_NAMES = new Set([
  'description',
  'keywords',
  'robots',
  'google-site-verification',
  'msvalidate.01',
])

/**
 * Property/name prefixes the emitter owns. Any meta whose `property` starts
 * with one of these (og:, article:) or whose `name` starts with `twitter:` is
 * considered managed and stripped before re-insertion.
 */
const MANAGED_PROPERTY_PREFIXES = ['og:', 'article:']
const MANAGED_NAME_PREFIXES = ['twitter:']

/** `rel=` values on `<link>` that the emitter owns and will replace. */
const MANAGED_LINK_RELS = new Set(['canonical', 'alternate'])

/**
 * IS THIS META TAG OURS? — the SINGLE definition of the managed meta set.
 *
 * WHY THIS IS EXPORTED AND WHY IT MUST STAY THE ONLY COPY (C-603 / Q-453=A).
 * `installSeoHead`'s DOM sweep removes the baked managed tags on the first
 * matched in-app navigation, and "managed" there has to mean EXACTLY what it
 * means here — this function is the build-time emitter's own answer, and the
 * runtime sweep is its DOM twin. The named failure mode of the sweep-once
 * design is precisely these two lists drifting: a tag the emitter starts baking
 * later would strand again, quietly, because the sweep would not recognise it.
 * A shared predicate makes that drift unrepresentable rather than merely
 * discouraged.
 *
 * Both arguments are accepted (rather than one "identity" string) because a
 * `<meta>` is keyed by `name` OR `property` and the two namespaces overlap:
 * `og:*` is a property, `twitter:*` is a name.
 */
export function isManagedMetaTag(
  name: string | null | undefined,
  property: string | null | undefined
): boolean {
  const n = typeof name === 'string' ? name.toLowerCase() : undefined
  const p = typeof property === 'string' ? property.toLowerCase() : undefined

  if (n) {
    if (MANAGED_META_NAMES.has(n)) return true
    if (MANAGED_NAME_PREFIXES.some((prefix) => n.startsWith(prefix))) return true
  }
  if (p) {
    if (MANAGED_PROPERTY_PREFIXES.some((prefix) => p.startsWith(prefix))) return true
  }
  return false
}

/**
 * IS THIS LINK OURS? — the SINGLE definition of the managed link set.
 *
 * `canonical` and `alternate` only. Stylesheets, preloads, icons and manifests
 * belong to the app shell and are never touched by either sink.
 */
export function isManagedLinkRel(rel: string | null | undefined): boolean {
  return typeof rel === 'string' && MANAGED_LINK_RELS.has(rel.toLowerCase())
}

/**
 * Render the resolved head tags to a deterministic HTML fragment.
 * Order: title, meta (in the order produced by buildHeadTags), link, script.
 */
export function renderHeadTags(tags: ResolvedHeadTags, indent = '    '): string {
  const lines: string[] = []

  lines.push(`${indent}<title>${escapeAttr(tags.title)}</title>`)

  for (const m of tags.meta) {
    if (m.property !== undefined) {
      lines.push(`${indent}<meta property="${escapeAttr(m.property)}" content="${escapeAttr(m.content)}" />`)
    } else if (m.name !== undefined) {
      lines.push(`${indent}<meta name="${escapeAttr(m.name)}" content="${escapeAttr(m.content)}" />`)
    }
  }

  for (const l of tags.link) {
    const hreflang = l.hreflang ? ` hreflang="${escapeAttr(l.hreflang)}"` : ''
    lines.push(`${indent}<link rel="${escapeAttr(l.rel)}" href="${escapeAttr(l.href)}"${hreflang} />`)
  }

  for (const s of tags.script) {
    lines.push(`${indent}<script type="${escapeAttr(s.type)}">${escapeJsonLd(s.children)}</script>`)
  }

  return lines.join('\n')
}

/**
 * Strip the SEO-managed tags from a `<head>` block so they can be re-inserted
 * without duplication. Operates only within `<head>...</head>` to avoid
 * touching body content.
 */
export function stripManagedHeadTags(html: string): string {
  const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)
  if (!headMatch) return html

  let head = headMatch[1]

  // Remove existing <title>…</title>
  head = head.replace(/[ \t]*<title>[\s\S]*?<\/title>[ \t]*\r?\n?/gi, '')

  // Remove existing JSON-LD scripts
  head = head.replace(
    /[ \t]*<script[^>]*type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>[ \t]*\r?\n?/gi,
    ''
  )

  // Remove managed <meta name="…"> and <meta property="…"> tags.
  // Matches any <meta ...> tag, inspects its name/property, drops it if managed.
  head = head.replace(/[ \t]*<meta\b[^>]*>[ \t]*\r?\n?/gi, (tag) => {
    const nameMatch = tag.match(/\bname=["']([^"']*)["']/i)
    const propMatch = tag.match(/\bproperty=["']([^"']*)["']/i)
    // Shared predicate — see `isManagedMetaTag`. The runtime DOM sweep in
    // `sweepBakedHeadTags.ts` asks this exact question of a real element, so
    // the build sink and the runtime sink cannot disagree about what is ours.
    return isManagedMetaTag(nameMatch?.[1], propMatch?.[1]) ? '' : tag
  })

  // Remove existing canonical link (alternates are also managed). Same shared
  // predicate as the meta pass — stylesheets/icons/preloads fall through.
  head = head.replace(/[ \t]*<link\b[^>]*>[ \t]*\r?\n?/gi, (tag) => {
    const relMatch = tag.match(/\brel=["']([^"']*)["']/i)
    return isManagedLinkRel(relMatch?.[1]) ? '' : tag
  })

  return html.slice(0, headMatch.index! + headMatch[0].indexOf(headMatch[1])) +
    head +
    html.slice(headMatch.index! + headMatch[0].indexOf(headMatch[1]) + headMatch[1].length)
}

/**
 * Splice resolved SEO head tags into an HTML document.
 *
 * Strips the existing managed tags, then inserts the rendered canonical set
 * immediately before `</head>`. If no `<head>` is present the HTML is returned
 * unchanged (defensive — the emitter logs and no-ops in that case).
 *
 * Idempotent: applying twice produces identical output.
 */
export function spliceHeadHtml(html: string, tags: ResolvedHeadTags): string {
  if (!/<\/head>/i.test(html)) {
    return html
  }

  const stripped = stripManagedHeadTags(html)
  const fragment = renderHeadTags(tags)

  // Insert before </head>, preserving the indentation of the closing tag.
  return stripped.replace(/([ \t]*)<\/head>/i, (_m, indent) => {
    return `${fragment}\n${indent}</head>`
  })
}
